import { createStorage } from 'unstorage'
import sessionStorageDriver from 'unstorage/drivers/session-storage'
import type { LocationApiResponse } from '../types'
import { LOCATION_ENDPOINT, LOCATION_STORAGE_KEY, APP_KEY } from '../constants'

class LocationStoreImpl {
  private storage = createStorage({
    driver: sessionStorageDriver({ base: APP_KEY }),
  })

  async loadLocation(): Promise<LocationApiResponse | null> {
    // Ler o storage PODE lançar, e não é caso exótico: Safari em navegação
    // privada, storage desabilitado por política, iframe com storage
    // particionado, quota estourada. Como `load()` faz `await` nesta função
    // antes de abrir o portão do `dispatch`, uma exceção aqui derrubava o
    // tracking inteiro em silêncio. Geolocalização é enriquecimento — a
    // ausência dela nunca deve custar os eventos.
    let location: LocationApiResponse | null = null
    try {
      location = await this.storage.getItem<LocationApiResponse>(LOCATION_STORAGE_KEY)
    } catch {
      // Sem cache disponível: segue para o fetch abaixo.
    }

    if (!location) {
      try {
        const response = await fetch(LOCATION_ENDPOINT, {
          headers: {
            'Content-Type': 'application/json',
            'X-Response-Case': 'snake_case',
          },
        })

        if (!response.ok) {
          throw new Error('Network response was not ok')
        }

        const apiData = await response.json() as LocationApiResponse
        // Guardar em cache é otimização e tem o mesmo risco da leitura. Se
        // ficasse no `try` de fora, uma escrita recusada (quota, storage
        // bloqueado) cairia no `catch` abaixo e DESCARTARIA um dado que já
        // estava em mãos.
        try {
          await this.storage.setItem(LOCATION_STORAGE_KEY, apiData)
        } catch {
          // Sem cache nesta visita; o valor abaixo segue válido.
        }
        location = apiData
      } catch {
        return null
      }
    }

    return location
  }

  async setLocation(location: LocationApiResponse): Promise<void> {
    // Mesmo motivo do `loadLocation`: escrever no storage pode lançar em
    // navegação privada / storage bloqueado / quota estourada. Este caminho vem
    // do `montilla.resolveLocation()` público, então uma rejeição aqui viraria
    // uma promise não tratada no app do consumidor. Guardar em cache é
    // otimização; não conseguir guardar não é erro que valha propagar.
    try {
      await this.storage.setItem(LOCATION_STORAGE_KEY, location)
    } catch {
      // Segue sem cache.
    }
  }
}

let instance: LocationStoreImpl | null = null

export function getLocationStore(): LocationStoreImpl {
  if (!instance) {
    instance = new LocationStoreImpl()
  }
  return instance
}

/** @internal Reset singleton for testing */
export function _resetLocationStore(): void {
  instance = null
}
